import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import './SimpleDatePicker.css';

/**
 * A simple date picker component with popup calendar
 * Works on all devices including mobile
 */
const SimpleDatePicker = ({ 
  selectedDate, 
  onDateChange, 
  minDate, 
  maxDate,
  label
}) => {
  // Parse date from YYYY-MM-DD format - MOVED here, before useState hooks that use it.
  const parseDate = useCallback((dateStr) => {
    if (!dateStr || typeof dateStr !== 'string' || !dateStr.includes('-')) {
      return new Date(); // Return current date if string is invalid or not YYYY-MM-DD
    }
    const parts = dateStr.split('-');
    if (parts.length !== 3) return new Date(); // Malformed string
  
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[2], 10);
  
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return new Date(); // Invalid date parts
    }
    return new Date(year, month, day);
  }, []);

  const [showCalendar, setShowCalendar] = useState(false);
  // Initialize with the correct month/year based on the selected date, using parseDate for strings
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = selectedDate ? (typeof selectedDate === 'string' ? parseDate(selectedDate) : new Date(selectedDate)) : new Date();
    return date.getMonth();
  });
  const [currentYear, setCurrentYear] = useState(() => {
    const date = selectedDate ? (typeof selectedDate === 'string' ? parseDate(selectedDate) : new Date(selectedDate)) : new Date();
    return date.getFullYear();
  });
  const [calendarDays, setCalendarDays] = useState([]);
  const calendarRef = useRef(null);
  
  // Month names for display
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  
  // Day names for the calendar header
  const dayNames = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
  
  // Format date as DD/MM/YYYY (European format)
  const formatDate = (dateInput) => {
    if (!dateInput) return '';
    // Use parseDate if it's a string (YYYY-MM-DD), otherwise treat as Date object or timestamp
    const d = typeof dateInput === 'string' ? parseDate(dateInput) : new Date(dateInput);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  // Format date as YYYY-MM-DD for input value
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };
  
  // Generate calendar days for the current month/year
  const generateCalendarDays = useCallback(() => {
    const days = [];
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    
    // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
    let firstDayOfWeek = firstDay.getDay();
    // Adjust for Monday as first day of week
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    // Add days from previous month
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const month = currentMonth - 1 < 0 ? 11 : currentMonth - 1;
      const year = month === 11 ? currentYear - 1 : currentYear;
      days.push({
        day,
        month,
        year,
        isCurrentMonth: false,
        date: new Date(year, month, day)
      });
    }
    
    // Add days from current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push({
        day,
        month: currentMonth,
        year: currentYear,
        isCurrentMonth: true,
        date: new Date(currentYear, currentMonth, day)
      });
    }
    
    // Add days from next month
    const daysNeeded = 42 - days.length; // 6 rows of 7 days
    for (let day = 1; day <= daysNeeded; day++) {
      const month = currentMonth + 1 > 11 ? 0 : currentMonth + 1;
      const year = month === 0 ? currentYear + 1 : currentYear;
      days.push({
        day,
        month,
        year,
        isCurrentMonth: false,
        date: new Date(year, month, day)
      });
    }
    
    setCalendarDays(days);
  }, [currentMonth, currentYear]);
  
  // Navigate to previous month
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  // Navigate to next month
  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  // Handle day click
  const handleDayClick = (day) => {
    const newDate = new Date(day.year, day.month, day.day);
    
    // Check if date is within min/max range
    if (minDate && newDate < parseDate(minDate)) return;
    if (maxDate && newDate > parseDate(maxDate)) return;
    
    onDateChange(formatDateForInput(newDate));
    setShowCalendar(false);
  };
  
  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on the calendar or if clicking on the overlay (which has its own click handler)
      if (calendarRef.current && !calendarRef.current.contains(event.target) && 
          !event.target.classList.contains('calendar-overlay')) {
        setShowCalendar(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);
  
  // Generate calendar days when month/year changes
  useEffect(() => {
    generateCalendarDays();
  }, [generateCalendarDays]);
  
  // Update current month/year when selected date changes
  useEffect(() => {
    let dateForCalendarView;
  
    if (selectedDate instanceof Date) {
      // If selectedDate is already a Date object, use it (clone to be safe)
      dateForCalendarView = new Date(selectedDate.getTime());
    } else if (typeof selectedDate === 'string' && selectedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // If selectedDate is a string in YYYY-MM-DD format (for robustness)
      dateForCalendarView = parseDate(selectedDate);
    } else {
      // Default: if selectedDate is null, undefined, or an unexpected format,
      // set the calendar view to the current system month/year.
      dateForCalendarView = new Date();
    }
  
    setCurrentMonth(dateForCalendarView.getMonth());
    setCurrentYear(dateForCalendarView.getFullYear());
  }, [selectedDate, parseDate]); // parseDate is stable due to useCallback
  
  return (
    <div className={`simple-date-picker ${showCalendar ? 'calendar-open' : ''}`}>
      {/* Overlay for mobile devices */}
      <div className="calendar-overlay" onClick={() => setShowCalendar(false)}></div>
      <label className="form-label">{label}</label>
      <div className="date-input-container">
        <input
          type="text"
          className="form-control"
          value={formatDate(selectedDate)}
          readOnly
          onClick={() => setShowCalendar(!showCalendar)}
        />
        <Button 
          variant="outline-secondary"
          className="calendar-toggle-btn"
          onClick={() => setShowCalendar(!showCalendar)}
        >
          <span role="img" aria-label="Calendar">
            📅
          </span>
        </Button>
      </div>
      
      {showCalendar && (
        <div className="calendar-popup" ref={calendarRef}>
          <div className="calendar-header">
            <button className="nav-btn" onClick={prevMonth}>&lt;</button>
            <div className="month-year">{monthNames[currentMonth]} {currentYear}</div>
            <button className="nav-btn" onClick={nextMonth}>&gt;</button>
          </div>
          
          <div className="calendar-days-header">
            {dayNames.map((day, index) => (
              <div key={index} className="day-name">{day}</div>
            ))}
          </div>
          
          <div className="calendar-days">
            {calendarDays.map((day, index) => {
              // Determine if this day is selected
              let parsedSelectedDateObj = null;
              if (selectedDate) {
                parsedSelectedDateObj = typeof selectedDate === 'string' ? parseDate(selectedDate) : new Date(selectedDate);
              }
              const isSelected = parsedSelectedDateObj && 
                day.day === parsedSelectedDateObj.getDate() &&
                day.month === parsedSelectedDateObj.getMonth() &&
                day.year === parsedSelectedDateObj.getFullYear();
              
              // Determine if this day is today
              const isToday = 
                day.day === new Date().getDate() &&
                day.month === new Date().getMonth() &&
                day.year === new Date().getFullYear();
              
              // Determine if this day is disabled (outside min/max range)
              const minDateObj = minDate ? parseDate(minDate) : null;
              const maxDateObj = maxDate ? parseDate(maxDate) : null;
              const isDisabled = 
                (minDateObj && day.date < minDateObj) || 
                (maxDateObj && day.date > maxDateObj);
              
              let dayClass = "calendar-day";
              if (!day.isCurrentMonth) dayClass += " other-month";
              if (isSelected) dayClass += " selected";
              if (isToday) dayClass += " today";
              if (isDisabled) dayClass += " disabled";
              
              return (
                <div 
                  key={index} 
                  className={dayClass}
                  onClick={() => !isDisabled && handleDayClick(day)}
                >
                  {day.day}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleDatePicker;
